import * as React from 'react';
import { Boxes, Cpu, Pencil, Plus, Trash2 } from 'lucide-react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import ProviderDialog from '@/components/settings/platform/v2/ProviderDialogV2';
import ModelDialog from '@/components/settings/platform/v2/ModelDialogV2';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ampm-7en-ai/ui/components/Accordion';
import { ListCard, ListRow, ListRowSkeleton } from '@ampm-7en-ai/ui/components/ListRow';
import { EmptyState } from '@ampm-7en-ai/ui/components/EmptyState';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { Switch } from '@ampm-7en-ai/ui/components/Switch';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Field } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { ConfirmDialog } from '@ampm-7en-ai/ui/components/ConfirmDialog';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import { useSuperAdminLLMProviders } from '@/hooks/useSuperAdminLLMProviders';
import type { ModelObject, SuperAdminLLMProvider } from '@/hooks/useLLMProviders';
import { useAgentPrompts } from '@/hooks/useAgentPrompts';
import { money } from '@/lib/money';

/**
 * The models the platform can call, and what each kind of agent is told
 * before it calls them.
 *
 * Two jobs on one page, so they are kept as two sections rather than
 * interleaved: the providers and their models, then the system prompt each
 * agent type starts from.
 */

/** Models come back as objects or bare names depending on the endpoint. */
const modelsOf = (provider: SuperAdminLLMProvider): ModelObject[] =>
  (Array.isArray(provider.models) ? provider.models : []).filter(
    (model): model is ModelObject => typeof model === 'object' && model !== null && 'id' in model
  );

/** The default model's name, whichever shape it arrived in. */
const defaultModelName = (provider: SuperAdminLLMProvider): string | null => {
  const chosen = provider.default_model;
  if (!chosen) return null;
  if (typeof chosen === 'string') return chosen;
  return chosen.display_name || chosen.name || null;
};

export default function LLMProvidersSettingsV2() {
  const { providers, isLoading, refetch, updateProvider, addModel, updateModel, deleteModel, deleteProvider } =
    useSuperAdminLLMProviders();
  const { prompts, isLoading: loadingPrompts, createPrompt, updatePrompt } = useAgentPrompts(false, true);

  const [addingProvider, setAddingProvider] = React.useState(false);
  const [editingProvider, setEditingProvider] = React.useState<SuperAdminLLMProvider | null>(null);
  const [modelDialog, setModelDialog] = React.useState<{ providerId: number; providerName: string; model: ModelObject | null } | null>(
    null
  );
  const [removingProvider, setRemovingProvider] = React.useState<SuperAdminLLMProvider | null>(null);
  const [removingModel, setRemovingModel] = React.useState<{ model: ModelObject; provider: string } | null>(null);

  /* ---------------------------------------------------------------- *
   * What each agent type is told
   * ---------------------------------------------------------------- */

  const [agentType, setAgentType] = React.useState('');
  const [systemPrompt, setSystemPrompt] = React.useState('');
  const [fallback, setFallback] = React.useState(true);
  const [savingPrompt, setSavingPrompt] = React.useState(false);

  const agentTypes = React.useMemo(
    () => Array.from(new Set(prompts.map((prompt) => prompt.agent_type))).sort(),
    [prompts]
  );
  const current = prompts.find((prompt) => prompt.agent_type === agentType);

  React.useEffect(() => {
    if (agentTypes.length > 0 && !agentType) setAgentType(agentTypes[0]);
  }, [agentTypes, agentType]);

  // Switching agent type loads what that one says, rather than keeping the
  // last one's text under a new heading.
  React.useEffect(() => {
    const prompt = prompts.find((one) => one.agent_type === agentType);
    setSystemPrompt(prompt?.system_prompt ?? '');
    setFallback(prompt?.enable_fallback ?? true);
  }, [agentType, prompts]);

  const promptChanged =
    systemPrompt !== (current?.system_prompt ?? '') || fallback !== (current?.enable_fallback ?? true);

  const savePrompt = async () => {
    if (!systemPrompt.trim() || !agentType) return;
    setSavingPrompt(true);
    try {
      const payload = { agent_type: agentType, system_prompt: systemPrompt, enable_fallback: fallback };
      if (current) await updatePrompt(current.id, payload);
      else await createPrompt(payload);
    } catch {
      // The hook raises its own toast; what was typed stays put.
    } finally {
      setSavingPrompt(false);
    }
  };

  return (
    <PlatformSettingsLayout title="Models" description="The providers the platform can call, and what each agent is told.">
      {/* ---------------- Providers ---------------- */}
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-ui-lg font-normal text-ui-fg">Providers</h2>
          <p className="mt-0.5 text-ui-sm text-ui-fg-muted">
            Switch one off and no agent can reach its models, whatever they are set to.
          </p>
        </div>
        <Button variant="primary" onClick={() => setAddingProvider(true)}>
          <Plus /> Add provider
        </Button>
      </div>

      {isLoading ? (
        <ListCard>
          {Array.from({ length: 3 }, (_, index) => (
            <li key={index}>
              <ListRowSkeleton />
            </li>
          ))}
        </ListCard>
      ) : providers.length === 0 ? (
        <Panel variant="dashed">
          <EmptyState
            icon={Boxes}
            title="No providers yet"
            description="Add one with its API key, and its models become available to every workspace."
            action={
              <Button variant="secondary" onClick={() => setAddingProvider(true)}>
                Add the first one
              </Button>
            }
          />
        </Panel>
      ) : (
        <Accordion type="multiple" className="space-y-3">
          {providers.map((provider) => {
            const models = modelsOf(provider);
            const fallbackName = defaultModelName(provider);
            return (
              <AccordionItem key={provider.id} value={String(provider.id)}>
                <AccordionTrigger>
                  <span className="flex min-w-0 flex-wrap items-center gap-2 text-ui-base">
                    <span className="truncate font-medium">{provider.provider_name}</span>
                    <Pill tone={provider.is_active ? 'success' : 'neutral'}>{provider.is_active ? 'On' : 'Off'}</Pill>
                    <span className="text-ui-xs text-ui-fg-subtle">
                      {models.length} {models.length === 1 ? 'model' : 'models'}
                    </span>
                    {fallbackName && <span className="text-ui-xs text-ui-fg-subtle">Default: {fallbackName}</span>}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-ui-sm text-ui-fg-muted">
                    <Switch
                      checked={provider.is_active}
                      aria-label={`${provider.provider_name} is ${provider.is_active ? 'on' : 'off'}`}
                      onCheckedChange={(next) =>
                        void updateProvider(provider.id, { is_active: next, status: next ? 'active' : 'inactive' })
                      }
                    />
                    Available to workspaces
                  </label>
                  <span className="flex items-center gap-1">
                    <Button variant="ghost" onClick={() => setEditingProvider(provider)}>
                      <Pencil /> Key
                    </Button>
                    <Button variant="ghost" className="text-ui-danger" onClick={() => setRemovingProvider(provider)}>
                      <Trash2 /> Remove
                    </Button>
                  </span>
                </div>

                {models.length === 0 ? (
                  <Panel variant="dashed" className="px-4 py-6">
                    <p className="text-center text-ui-sm text-ui-fg-muted">
                      No models on this provider yet.
                    </p>
                  </Panel>
                ) : (
                  <ListCard>
                    {models.map((model) => {
                      const input = model.cost_per_1m_input_tokens == null ? null : `${money(model.cost_per_1m_input_tokens)}/1M`;
                      const output = model.cost_per_1m_output_tokens == null ? null : `${money(model.cost_per_1m_output_tokens)}/1M`;
                      return (
                        <li key={model.id}>
                          <ListRow
                            leading={<Cpu className="mt-0.5 h-4 w-4 text-ui-fg-subtle" aria-hidden />}
                            title={<span className="truncate">{model.display_name || model.name}</span>}
                            description={
                              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="font-ui-mono text-ui-xs text-ui-fg-subtle">{model.name}</span>
                                {input && <span className="text-ui-xs text-ui-fg-subtle">In {input}</span>}
                                {output && <span className="text-ui-xs text-ui-fg-subtle">Out {output}</span>}
                              </span>
                            }
                            meta={model.is_premium ? <Pill tone="accent">Premium</Pill> : undefined}
                            trailing={
                              <span className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  aria-label={`Edit ${model.display_name || model.name}`}
                                  onClick={() =>
                                    setModelDialog({ providerId: provider.id, providerName: provider.provider_name, model })
                                  }
                                >
                                  <Pencil />
                                </Button>
                                <Button
                                  variant="ghost"
                                  aria-label={`Delete ${model.display_name || model.name}`}
                                  className="text-ui-danger"
                                  onClick={() => setRemovingModel({ model, provider: provider.provider_name })}
                                >
                                  <Trash2 />
                                </Button>
                              </span>
                            }
                          />
                        </li>
                      );
                    })}
                  </ListCard>
                )}

                <Button
                  variant="ghost"
                  className="mt-2"
                  onClick={() => setModelDialog({ providerId: provider.id, providerName: provider.provider_name, model: null })}
                >
                  <Plus /> Add a model
                </Button>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* ---------------- Agent prompts ---------------- */}
      <section className="mt-10">
        <h2 className="text-ui-lg font-normal text-ui-fg">Agent prompts</h2>
        <p className="mb-3 mt-0.5 text-ui-sm text-ui-fg-muted">
          What every agent of a kind is told before anything else. Workspaces write their own on top of this.
        </p>

        <Panel padding="md">
          {loadingPrompts ? (
            <div className="space-y-4">
              <Skeleton className="h-[38px] w-56 rounded-ui-md" />
              <Skeleton className="h-40 w-full rounded-ui-md" />
            </div>
          ) : agentTypes.length === 0 ? (
            <EmptyState
              icon={Boxes}
              title="No agent types yet"
              description="Once the platform defines a kind of agent, its starting prompt is written here."
            />
          ) : (
            <div className="space-y-5">
              <Field label="Agent type" htmlFor="agent-type" className="max-w-xs">
                <Select
                  id="agent-type"
                  options={agentTypes.map((type) => ({ value: type, label: type }))}
                  value={agentType}
                  onValueChange={setAgentType}
                />
              </Field>

              <Field
                label="System prompt"
                htmlFor="system-prompt"
                hint="Every agent of this type starts from these words."
              >
                <Textarea
                  id="system-prompt"
                  rows={10}
                  value={systemPrompt}
                  onChange={(event) => setSystemPrompt(event.target.value)}
                  placeholder="You are a support agent for…"
                  className="font-ui-mono text-ui-sm"
                />
              </Field>

              <ToggleRow
                label="Fall back to another model"
                description="If the chosen model can’t be reached, fallback to GPT 3.5 Turbo rather than failing the conversation."
                checked={fallback}
                onCheckedChange={setFallback}
              />

              <div className="flex items-center justify-end gap-3">
                {promptChanged && !savingPrompt && <p className="text-ui-xs text-ui-fg-subtle">Unsaved changes</p>}
                <Button
                  variant="primary"
                  onClick={() => void savePrompt()}
                  disabled={savingPrompt || !systemPrompt.trim() || !promptChanged}
                >
                  {savingPrompt ? 'Saving…' : current ? 'Save prompt' : 'Create prompt'}
                </Button>
              </div>
            </div>
          )}
        </Panel>
      </section>

      {/* ---------------- Dialogs ---------------- */}
      <ProviderDialog open={addingProvider} onOpenChange={setAddingProvider} onAdded={refetch} />

      <ProviderDialog
        open={editingProvider !== null}
        onOpenChange={(open) => !open && setEditingProvider(null)}
        provider={editingProvider}
        onUpdated={updateProvider}
      />

      <ModelDialog
        open={modelDialog !== null}
        onOpenChange={(open) => !open && setModelDialog(null)}
        providerId={modelDialog?.providerId ?? 0}
        providerName={modelDialog?.providerName ?? ''}
        model={modelDialog?.model ?? null}
        onAdd={addModel}
        onUpdate={updateModel}
      />

      <ConfirmDialog
        open={removingProvider !== null}
        onOpenChange={(open) => !open && setRemovingProvider(null)}
        title={`Remove ${removingProvider?.provider_name ?? 'this provider'}?`}
        description="Its models go with it, and any agent set to one of them stops working. This can’t be undone."
        confirmLabel="Remove provider"
        tone="danger"
        onConfirm={async () => {
          if (!removingProvider) return;
          try {
            await deleteProvider(removingProvider.id);
          } finally {
            setRemovingProvider(null);
          }
        }}
      />

      <ConfirmDialog
        open={removingModel !== null}
        onOpenChange={(open) => !open && setRemovingModel(null)}
        title={`Delete ${removingModel?.model.display_name || removingModel?.model.name || 'this model'}?`}
        description={`Agents on ${removingModel?.provider ?? 'this provider'} set to it will need another. This can’t be undone.`}
        confirmLabel="Delete model"
        tone="danger"
        onConfirm={async () => {
          if (!removingModel) return;
          try {
            await deleteModel(removingModel.model.id);
          } finally {
            setRemovingModel(null);
          }
        }}
      />
    </PlatformSettingsLayout>
  );
}
